import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DownloadIcon from '@mui/icons-material/Download';
import UploadIcon from '@mui/icons-material/Upload';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { FC, useMemo, useRef, useState } from 'react';
import { useTranslations } from 'use-intl';

import {
  formatHotkey,
  hasModifier,
  isReservedCombo,
} from '../hotkeys';
import useNavigationLink from '../../../hooks/useNavigationLink';
import { useHotkeySettings } from '../context/HotkeySettings';
import { getNavigablePages, NavigablePage } from '../utils/pages';
import { NoValidShortcutsError } from '../services/storage';
import ShortcutRecorder, { ShortcutCombo } from './ShortcutRecorder';

interface HotkeySettingsDialogProps {
  onClose: () => void;
}

const HotkeySettingsDialog: FC<HotkeySettingsDialogProps> = ({ onClose }) => {
  const t = useTranslations('hotkeySettings.dialog');
  const {
    settings,
    setEnabled,
    setShowBadges,
    addHotkey,
    removeHotkey,
    resetDefaults,
    exportSettings,
    importSettings,
    isComboTaken,
  } = useHotkeySettings();
  const { navigationLinks } = useNavigationLink();

  const pages = useMemo(() => getNavigablePages(navigationLinks), [navigationLinks]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [selectedPage, setSelectedPage] = useState<NavigablePage | null>(null);
  const [combo, setCombo] = useState<ShortcutCombo | undefined>(undefined);
  const [addError, setAddError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const handleAdd = () => {
    setNotice(null);
    if (!selectedPage) return setAddError(t('errors.pickPage'));
    if (!combo?.code) return setAddError(t('errors.recordShortcut'));
    if (!hasModifier(combo))
      return setAddError(t('errors.needModifier'));
    if (isReservedCombo(combo))
      return setAddError(t('errors.reserved', { combo: formatHotkey(combo) }));
    if (isComboTaken(combo))
      return setAddError(t('errors.taken', { combo: formatHotkey(combo) }));

    addHotkey({ ...combo, path: selectedPage.path, label: selectedPage.label });
    setSelectedPage(null);
    setCombo(undefined);
    setAddError(null);
  };

  const handleImportClick = () => fileInputRef.current?.click();

  const handleImportFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = ''; // allow re-importing the same file
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        importSettings(String(reader.result));
        setNotice(t('imported'));
        setAddError(null);
      } catch (error) {
        setAddError(
          error instanceof NoValidShortcutsError
            ? t('errors.noValidShortcuts')
            : error instanceof Error
            ? error.message
            : t('errors.importFailed')
        );
      }
    };
    reader.readAsText(file);
  };

  return (
    <Dialog open onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
      >
        {t('title')}
        <IconButton onClick={onClose} size='small'>
          <CloseIcon fontSize='small' />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={1.5}>
          <FormControlLabel
            sx={{ '& .MuiFormControlLabel-label': { ml: 1 } }}
            control={
              <Switch
                checked={settings.enabled}
                onChange={(_, checked) => setEnabled(checked)}
              />
            }
            label={t('enable')}
          />
          <FormControlLabel
            sx={{ '& .MuiFormControlLabel-label': { ml: 1 } }}
            control={
              <Switch
                checked={settings.showBadges}
                disabled={!settings.enabled}
                onChange={(_, checked) => setShowBadges(checked)}
              />
            }
            label={t('showBadges')}
          />
        </Stack>

        <Divider sx={{ my: 2 }} />

        <Typography variant='subtitle2' sx={{ mb: 1 }}>
          {t('addTitle')}
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems='center'>
          <Autocomplete
            fullWidth
            size='small'
            options={pages}
            value={selectedPage}
            onChange={(_, value) => setSelectedPage(value)}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(a, b) => a.path === b.path}
            renderInput={(params) => (
              <TextField {...params} label={t('page')} placeholder={t('searchPages')} />
            )}
          />
          <ShortcutRecorder
            value={combo}
            onChange={(value) => {
              setCombo(value);
              setAddError(null);
            }}
          />
          <Button variant='contained' onClick={handleAdd} sx={{ whiteSpace: 'nowrap' }}>
            {t('add')}
          </Button>
        </Stack>

        {addError && (
          <Alert severity='warning' sx={{ mt: 1.5 }} onClose={() => setAddError(null)}>
            {addError}
          </Alert>
        )}
        {notice && (
          <Alert severity='success' sx={{ mt: 1.5 }} onClose={() => setNotice(null)}>
            {notice}
          </Alert>
        )}

        <Divider sx={{ my: 2 }} />

        <Typography variant='subtitle2' sx={{ mb: 1 }}>
          {t('current', { count: String(settings.hotkeys.length) })}
        </Typography>
        {settings.hotkeys.length === 0 ? (
          <Typography variant='body2' color='text.secondary'>
            {t('empty')}
          </Typography>
        ) : (
          <List dense disablePadding>
            {settings.hotkeys.map((hotkey, index) => (
              <ListItem
                key={`${hotkey.path}-${formatHotkey(hotkey)}`}
                disableGutters
                secondaryAction={
                  <Tooltip title={t('remove')}>
                    <IconButton edge='end' size='small' onClick={() => removeHotkey(index)}>
                      <DeleteOutlineIcon fontSize='small' />
                    </IconButton>
                  </Tooltip>
                }
              >
                <ListItemText
                  primary={hotkey.label}
                  secondary={hotkey.path}
                  primaryTypographyProps={{ variant: 'body2' }}
                  secondaryTypographyProps={{ variant: 'caption' }}
                />
                <Chip
                  label={formatHotkey(hotkey)}
                  size='small'
                  variant='outlined'
                  sx={{ mr: 5 }}
                />
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'space-between', px: 3, py: 1.5, flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button startIcon={<UploadIcon />} onClick={handleImportClick} color='inherit'>
            {t('import')}
          </Button>
          <Button startIcon={<DownloadIcon />} onClick={exportSettings} color='inherit'>
            {t('export')}
          </Button>
          <Button startIcon={<RestartAltIcon />} onClick={resetDefaults} color='inherit'>
            {t('reset')}
          </Button>
          <input
            ref={fileInputRef}
            type='file'
            accept='application/json,.json'
            hidden
            onChange={handleImportFile}
          />
        </Box>
        <Button variant='contained' onClick={onClose}>
          {t('done')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default HotkeySettingsDialog;
